import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import { clientAPI, analyticsAPI, invoiceAPI, auditAPI } from '../../../services';
import { LoadingSpinner } from '../../UI';
import { Building2, CircleDollarSign, Megaphone, History, AlertTriangle } from 'lucide-react';
import {
  C, FONT_UI, Mono, Card, StatCard, SectionCard, AttentionBanner, Pill,
  OrgAvatar, EmptyState, fmtINR, fmtNum, fmtRelativeStamp, daysUntil,
} from './theme';

const MONTH_KEY = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

// Last six month buckets, oldest first — the window every trend line uses.
const lastSixMonths = () => {
  const out = [];
  const now = new Date();
  for (let i = 5; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push({ key: MONTH_KEY(d), label: d.toLocaleDateString('en-US', { month: 'short' }), date: d });
  }
  return out;
};

const ACTION_TONE = {
  create: C.emerald,
  update: C.signal,
  delete: C.rose,
  payment: C.emerald,
  status_change: C.rose,
  send: C.violet,
  login: C.textFaint,
  logout: C.textFaint,
};

const SuperAdminOverview = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [clients, setClients] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [invoiceStats, setInvoiceStats] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [logs, setLogs] = useState([]);

  useEffect(() => {
    const fetchAll = async () => {
      const safe = (p) => p.then((r) => r).catch(() => null);
      const sixMonthsAgo = new Date();
      sixMonthsAgo.setMonth(sixMonthsAgo.getMonth() - 5, 1);

      const [clientsRes, analyticsRes, statsRes, invoicesRes, logsRes] = await Promise.all([
        safe(clientAPI.getClients({ page: 1, limit: 100 })),
        safe(analyticsAPI.getSystemAnalytics()),
        safe(invoiceAPI.getStats()),
        safe(invoiceAPI.getInvoices({ startDate: sixMonthsAgo.toISOString(), limit: 200 })),
        safe(auditAPI.getLogs({ limit: 5 })),
      ]);

      setClients(clientsRes?.data?.clients || []);
      setAnalytics(analyticsRes?.data || null);
      setInvoiceStats(statsRes?.data || null);
      setInvoices(invoicesRes?.data?.invoices || []);
      setLogs(logsRes?.data?.logs || []);
      setLoading(false);
    };
    fetchAll();
  }, []);

  const months = useMemo(lastSixMonths, []);

  // Billed / outstanding per month, bucketed from the invoice window above.
  const { revenueSeries, outstandingSeries, revenueThisMonth, revenueLastMonth } = useMemo(() => {
    const billed = Object.fromEntries(months.map((m) => [m.key, 0]));
    const owed = Object.fromEntries(months.map((m) => [m.key, 0]));
    invoices.forEach((inv) => {
      if (inv.status === 'cancelled') return;
      const key = MONTH_KEY(new Date(inv.issueDate));
      if (key in billed) {
        billed[key] += Number(inv.total) || 0;
        owed[key] += Number(inv.balance) || 0;
      }
    });
    const rev = months.map((m) => billed[m.key]);
    return {
      revenueSeries: rev,
      outstandingSeries: months.map((m) => owed[m.key]),
      revenueThisMonth: rev[rev.length - 1] || 0,
      revenueLastMonth: rev[rev.length - 2] || 0,
    };
  }, [invoices, months]);

  const clientSeries = useMemo(() => {
    const growth = analytics?.growth?.monthlyGrowth || [];
    if (!growth.length) return null;
    let running = 0;
    return growth.map((g) => (running += g.count || 0));
  }, [analytics]);

  const totalClients = analytics?.overview?.totalClients ?? clients.length;
  const totalUsers =
    analytics?.overview?.totalUsers ?? clients.reduce((s, c) => s + (c.userCount || 0), 0);
  const clientDelta = analytics?.growth?.clientGrowth?.changePercentage;

  const revenueDelta =
    revenueLastMonth > 0
      ? `${revenueThisMonth >= revenueLastMonth ? '↗ +' : '↘ '}${(
          ((revenueThisMonth - revenueLastMonth) / revenueLastMonth) * 100
        ).toFixed(1)}%`
      : null;

  const suspended = clients.filter((c) => !c.active);
  const trialsEndingSoon = clients.filter((c) => {
    if (c.subscription?.type !== 'trial') return false;
    const d = daysUntil(c.subscription?.trialEndDate);
    return d !== null && d >= 0 && d <= 7;
  });
  const overdue = invoices.filter(
    (i) => Number(i.balance) > 0 && new Date(i.dueDate) < new Date() && !['draft', 'cancelled', 'paid'].includes(i.status)
  );

  const attention = [];
  if (overdue.length) {
    attention.push({
      title: `${overdue.length} invoice${overdue.length > 1 ? 's' : ''} overdue`,
      detail: `${overdue[0].client?.name || 'Client'} · ${fmtINR(overdue[0].balance)}`,
      onClick: () => navigate('/dashboard/billing'),
    });
  }
  if (suspended.length) {
    attention.push({
      title: `${suspended.length} client${suspended.length > 1 ? 's' : ''} suspended`,
      detail: suspended.map((c) => c.name).slice(0, 2).join(', '),
      onClick: () => navigate('/dashboard/clients'),
    });
  }
  if (trialsEndingSoon.length) {
    attention.push({
      title: `${trialsEndingSoon.length} trial${trialsEndingSoon.length > 1 ? 's' : ''} ending this week`,
      detail: 'Send a renewal reminder',
      onClick: () => navigate('/dashboard/clients'),
    });
  }

  const recentClients = [...clients]
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    .slice(0, 4);

  const quickActions = [
    { label: 'Manage clients', icon: Building2, tone: C.signal, to: '/dashboard/clients' },
    { label: 'Create invoice', icon: CircleDollarSign, tone: C.emerald, to: '/dashboard/billing' },
    { label: 'Send announcement', icon: Megaphone, tone: C.violet, to: '/dashboard/announcements' },
    { label: 'View audit log', icon: History, tone: C.textMuted, to: '/dashboard/audit-log' },
  ];

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return (
    <div className="space-y-5" style={{ fontFamily: FONT_UI }}>
      {/* Greeting */}
      <div>
        <Mono className="text-[10px] font-medium uppercase tracking-[0.14em]" style={{ color: C.textFaint }}>
          {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' })}
        </Mono>
        <h1 className="text-[26px] font-bold mt-1.5" style={{ color: C.ink }}>
          Welcome back, {user?.name || 'Super Administrator'}
        </h1>
        <p className="text-[13px] mt-1" style={{ color: C.textMuted }}>
          Here's the platform's pulse for the last 6 months.
        </p>
      </div>

      <AttentionBanner items={attention} icon={AlertTriangle} />

      {/* Stat row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
        <StatCard
          label="Total clients"
          value={fmtNum(totalClients)}
          delta={clientDelta != null ? `${Number(clientDelta) >= 0 ? '↗ +' : '↘ '}${clientDelta}%` : null}
          deltaTone={Number(clientDelta) >= 0 ? 'up' : 'down'}
          spark={clientSeries}
          sparkColor={C.signal}
        />
        <StatCard
          label="Total users"
          value={fmtNum(totalUsers)}
          hint={`Across ${fmtNum(totalClients)} organization${totalClients === 1 ? '' : 's'}`}
        />
        <StatCard
          label="Revenue this month"
          value={fmtINR(revenueThisMonth)}
          delta={revenueDelta}
          deltaTone={revenueThisMonth >= revenueLastMonth ? 'up' : 'down'}
          spark={revenueSeries}
          sparkColor={C.emerald}
        />
        <StatCard
          label="Outstanding"
          value={fmtINR(invoiceStats?.outstanding ?? 0)}
          delta={invoiceStats?.overdueCount ? `↘ ${invoiceStats.overdueCount} overdue` : null}
          deltaTone={invoiceStats?.overdueCount ? 'down' : 'flat'}
          spark={outstandingSeries}
          sparkColor={C.amber}
        />
      </div>

      {/* Quick actions */}
      <SectionCard title="Quick actions" bodyClassName="p-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {quickActions.map((a) => {
            const Icon = a.icon;
            return (
              <Link
                key={a.label}
                to={a.to}
                className="flex flex-col items-center justify-center gap-2 py-5 rounded-xl transition-colors hover:bg-slate-50"
                style={{ border: `1px solid ${C.border}` }}
              >
                <Icon className="w-[18px] h-[18px]" style={{ color: a.tone }} />
                <span className="text-[12px] font-medium" style={{ color: C.ink }}>
                  {a.label}
                </span>
              </Link>
            );
          })}
        </div>
      </SectionCard>

      {/* Recent clients + activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <SectionCard
          title="Recent clients"
          action={
            <Link to="/dashboard/clients" className="text-[12px] font-medium" style={{ color: C.signal }}>
              View all
            </Link>
          }
        >
          {recentClients.length ? (
            recentClients.map((c, i) => (
              <div
                key={c._id}
                className="flex items-center justify-between gap-3 px-5 py-3"
                style={{ borderTop: i ? `1px solid ${C.border}` : 'none' }}
              >
                <div className="flex items-center gap-3 min-w-0">
                  <OrgAvatar name={c.name} logoUrl={c.logo?.url} size={34} radius={10} />
                  <div className="min-w-0">
                    <p className="text-[13px] font-medium truncate" style={{ color: C.ink }}>
                      {c.name}
                    </p>
                    <Mono className="block text-[11px] truncate" style={{ color: C.textMuted }}>
                      {c.email || c.code || '—'}
                    </Mono>
                  </div>
                </div>
                {!c.active ? (
                  <Pill tone="danger">Suspended</Pill>
                ) : c.subscription?.type === 'trial' ? (
                  <Pill tone="success">Trial</Pill>
                ) : (
                  <Pill tone="info">Paid</Pill>
                )}
              </div>
            ))
          ) : (
            <EmptyState icon={Building2} title="No clients yet" message="Add your first client to get started." />
          )}
        </SectionCard>

        <SectionCard
          title="Recent activity"
          action={
            <Link to="/dashboard/audit-log" className="text-[12px] font-medium" style={{ color: C.signal }}>
              View audit log
            </Link>
          }
        >
          {logs.length ? (
            logs.map((l, i) => (
              <div
                key={l._id || i}
                className="flex items-start gap-3 px-5 py-3"
                style={{ borderTop: i ? `1px solid ${C.border}` : 'none' }}
              >
                <span
                  className="w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0"
                  style={{ background: ACTION_TONE[l.action] || C.textFaint }}
                />
                <div className="min-w-0">
                  <p className="text-[13px] truncate" style={{ color: C.ink }}>
                    {l.description || `${l.action} — ${l.module}`}
                    {l.entityLabel ? ` — ${l.entityLabel}` : ''}
                  </p>
                  <Mono className="block text-[11px] mt-0.5" style={{ color: C.textMuted }}>
                    {fmtRelativeStamp(l.createdAt)}
                  </Mono>
                </div>
              </div>
            ))
          ) : (
            <EmptyState icon={History} title="No activity yet" message="Platform actions will appear here." />
          )}
        </SectionCard>
      </div>

      {/* Trend footnote card kept for parity with the finance pages */}
      <Card className="px-5 py-3 flex flex-wrap items-center gap-x-6 gap-y-1">
        {months.map((m, i) => (
          <span key={m.key} className="text-[11px]" style={{ color: C.textMuted }}>
            {m.label}{' '}
            <Mono style={{ color: C.ink }}>{fmtINR(revenueSeries[i])}</Mono>
          </span>
        ))}
      </Card>
    </div>
  );
};

export default SuperAdminOverview;
